import { useState, useRef, useEffect, useCallback } from "react";
import { Media } from "@/types";
import { baseApi } from "@/store/baseApi";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from "lucide-react";

interface PhotoUploadSectionProps {
  moveDigest: string;
  photos: Media[];
}

const PhotoUploadSection = ({
  moveDigest,
  photos,
}: PhotoUploadSectionProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localPhotos, setLocalPhotos] = useState<Media[]>(photos);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(
    null
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  // Create endpoints for handling photos
  const photoApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
      getPhotoSignedUrl: builder.mutation<
        { data: { signed_url: string; key: string } },
        { filename: string; content_type: string; move_digest: string }
      >({
        query: (body) => ({
          url: `/customer-hub/move/${moveDigest}/signed_url`,
          method: "POST",
          body,
        }),
      }),
      getMovePhotos: builder.query<{ media: Media[] }, string>({
        query: (digest) => `/customer-hub/move/${digest}/photos`,
      }),
      updateMedia: builder.mutation<
        { success: boolean; media?: Media },
        {
          move_digest: string;
          media: {
            name: string;
            file_path: string;
            file_content_type: string;
            file_size: number;
            file_name: string;
          };
        }
      >({
        query: (body) => ({
          url: `/customer-hub/move/${body.move_digest}/update_media`,
          method: "POST",
          body: body.media,
        }),
      }),
    }),
  });

  const [getPhotoSignedUrl] =
    photoApi.endpoints.getPhotoSignedUrl.useMutation();
  const [updateMedia] = photoApi.endpoints.updateMedia.useMutation();
  const { data: photoData, refetch: refetchPhotos } =
    photoApi.endpoints.getMovePhotos.useQuery(moveDigest, {
      skip: !moveDigest,
    });

  useEffect(() => {
    if (photoData?.media) {
      setLocalPhotos(photoData.media);
    }
  }, [photoData]);

  useEffect(() => {
    setLocalPhotos(photos);
  }, [photos]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];

      // Validate file type (only allow images)
      if (!selectedFile.type.startsWith("image/")) {
        alert("Please select an image file (PNG, JPG, etc.)");
        return;
      }

      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || !moveDigest) return;

    try {
      setIsUploading(true);

      // Get signed URL
      const signedUrlResponse = await getPhotoSignedUrl({
        filename: file.name,
        content_type: file.type,
        move_digest: moveDigest,
      }).unwrap();

      if (!signedUrlResponse.data) {
        throw new Error("Failed to get signed URL");
      }

      const { signed_url, key } = signedUrlResponse.data;

      // Upload to S3
      const response = await fetch(signed_url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error("Failed to upload photo");
      }

      // Update the media record with additional metadata
      await updateMedia({
        move_digest: moveDigest,
        media: {
          name: file.name.split(".")[0], // Use filename without extension as name
          file_path: key,
          file_content_type: file.type,
          file_size: file.size,
          file_name: file.name,
        },
      });

      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setFile(null);

      // Refresh the photos list
      refetchPhotos();
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload photo. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!dialogOpen || localPhotos.length <= 1 || selectedPhotoIndex === null)
        return;

      if (e.key === "ArrowRight") {
        // Navigate to next photo
        setSelectedPhotoIndex((selectedPhotoIndex + 1) % localPhotos.length);
        e.preventDefault();
      } else if (e.key === "ArrowLeft") {
        // Navigate to previous photo
        setSelectedPhotoIndex(
          (selectedPhotoIndex - 1 + localPhotos.length) % localPhotos.length
        );
        e.preventDefault();
      }
    },
    [dialogOpen, selectedPhotoIndex, localPhotos]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const navigateToNextPhoto = () => {
    if (selectedPhotoIndex !== null && localPhotos.length > 1) {
      setSelectedPhotoIndex((selectedPhotoIndex + 1) % localPhotos.length);
    }
  };

  const navigateToPrevPhoto = () => {
    if (selectedPhotoIndex !== null && localPhotos.length > 1) {
      setSelectedPhotoIndex(
        (selectedPhotoIndex - 1 + localPhotos.length) % localPhotos.length
      );
    }
  };

  return (
    <div className="p-4 bg-white flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-medium">Photos</h2>
        <h4 className="text-sm">
          Upload photos of your items or rooms to help us better prepare for
          your move. Only image files are accepted.
        </h4>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      <div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
          {localPhotos.map((photo, index) => (
            <Dialog
              key={photo.id}
              open={dialogOpen && selectedPhotoIndex === index}
              onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) setSelectedPhotoIndex(null);
              }}
            >
              <DialogTrigger asChild>
                <div
                  className="relative group aspect-square overflow-hidden transition-shadow duration-200 max-w-[120px] cursor-pointer"
                  onClick={() => setSelectedPhotoIndex(index)}
                >
                  <img
                    src={photo.file_url}
                    alt="Move item"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity duration-200" />
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <div className="flex justify-center items-center relative">
                  {localPhotos.length > 1 && (
                    <>
                      <button
                        onClick={navigateToPrevPhoto}
                        className="absolute left-0 z-10 bg-black/30 hover:bg-black/50 text-white p-2 rounded-full m-2"
                        aria-label="Previous photo"
                      >
                        <ChevronLeftIcon className="w-6 h-6" />
                      </button>

                      <button
                        onClick={navigateToNextPhoto}
                        className="absolute right-0 z-10 bg-black/30 hover:bg-black/50 text-white p-2 rounded-full m-2"
                        aria-label="Next photo"
                      >
                        <ChevronRightIcon className="w-6 h-6" />
                      </button>
                    </>
                  )}

                  {selectedPhotoIndex !== null && (
                    <img
                      src={localPhotos[selectedPhotoIndex].file_url}
                      alt={localPhotos[selectedPhotoIndex].name || "Move item"}
                      className="max-h-[80vh] object-contain"
                    />
                  )}
                </div>
              </DialogContent>
            </Dialog>
          ))}
          <div
            onClick={triggerFileInput}
            className="text-center bg-yellow-50 w-120 h-120 border-2 border-dashed border-yellow-200 cursor-pointer flex items-center justify-center"
          >
            <PlusIcon className="w-12 h-12" />
          </div>
        </div>
      </div>

      {/* File upload status */}
      {file && (
        <div className="w-full md:w-[300px] flex flex-col gap-2">
          <img
            src={window.URL.createObjectURL(file)}
            className="w-120 max-w-[300px] max-h-[300px] aspect-square object-cover p-2 border border-stone-200 rounded-md"
          />
          <Button
            onClick={handleUpload}
            disabled={isUploading}
            className="bg-blue-600 hover:bg-blue-700 text-white text-base"
          >
            {isUploading ? "Uploading..." : "Confirm Upload"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PhotoUploadSection;
